/**
 * Vault Key Store - Zustand
 *
 * Gère les clés maîtres des vaults déverrouillés EN MÉMOIRE UNIQUEMENT.
 * Les clés ne sont JAMAIS persistées dans localStorage/sessionStorage pour des raisons de sécurité.
 *
 * Fonctionnalités:
 * - Stocker les clés maîtres des vaults déverrouillés
 * - Auto-lock après 30 minutes d'inactivité
 * - Verrouillage automatique à la fermeture de la page
 *
 * @module vaultKeyStore
 */

import { create } from 'zustand';

interface VaultKey {
  vaultId: string;
  masterKey: CryptoKey;
  unlockedAt: Date;
}

interface VaultKeyStore {
  unlockedVaults: Map<string, VaultKey>;
  unlockVault: (vaultId: string, masterKey: CryptoKey) => void;
  lockVault: (vaultId: string) => void;
  lockAllVaults: () => void;
  isVaultUnlocked: (vaultId: string) => boolean;
  getMasterKey: (vaultId: string) => CryptoKey | null;
}

export const useVaultKeyStore = create<VaultKeyStore>((set, get) => ({
  unlockedVaults: new Map(),

  /**
   * Déverrouiller un vault et stocker sa clé maître en mémoire
   */
  unlockVault: (vaultId: string, masterKey: CryptoKey) => {
    set((state) => {
      const newMap = new Map(state.unlockedVaults);
      newMap.set(vaultId, {
        vaultId,
        masterKey,
        unlockedAt: new Date(),
      });
      return { unlockedVaults: newMap };
    });
  },

  /**
   * Verrouiller un vault spécifique (supprimer sa clé de la mémoire)
   */
  lockVault: (vaultId: string) => {
    set((state) => {
      const newMap = new Map(state.unlockedVaults);
      newMap.delete(vaultId);
      return { unlockedVaults: newMap };
    });
  },

  /**
   * Verrouiller tous les vaults (supprimer toutes les clés de la mémoire)
   */
  lockAllVaults: () => {
    set({ unlockedVaults: new Map() });
  },

  /**
   * Vérifier si un vault est déverrouillé
   */
  isVaultUnlocked: (vaultId: string) => {
    return get().unlockedVaults.has(vaultId);
  },

  /**
   * Récupérer la clé maître d'un vault déverrouillé
   */
  getMasterKey: (vaultId: string) => {
    const vaultKey = get().unlockedVaults.get(vaultId);
    return vaultKey?.masterKey || null;
  },
}));

// Auto-lock après 30 minutes d'inactivité
if (typeof window !== 'undefined') {
  let inactivityTimer: NodeJS.Timeout;

  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(
      () => {
        useVaultKeyStore.getState().lockAllVaults();
        console.warn('[Vault] Auto-locked due to 30 minutes of inactivity');
      },
      30 * 60 * 1000,
    ); // 30 minutes
  };

  // Réinitialiser le timer sur activité utilisateur
  ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach((event) => {
    window.addEventListener(event, resetInactivityTimer, true);
  });

  // Démarrer le timer initial
  resetInactivityTimer();

  // Verrouiller tous les vaults lors de la fermeture de la page
  window.addEventListener('beforeunload', () => {
    useVaultKeyStore.getState().lockAllVaults();
  });
}
